import { useEffect, useState } from "react";
import { AlertCircle, Check, CreditCard, LoaderCircle, Pencil, Rocket, RotateCw } from "lucide-react";
import { api } from "../lib/api";

type DraftCampaign = {
  id: string;
  title: string;
  status: string;
  updated_at: string | null;
};

type DraftCampaignsResponse = { campaigns: DraftCampaign[] };
type LaunchCampaignResponse = { campaign: DraftCampaign };

type LaunchFailureKind = "billing" | "config" | "setup" | "generic";

// The ad network rejects a launch with a human-readable reason, but the creator
// can only toast it — and that toast dies in the iframe. Now that the reason
// reaches us, classify it so the user gets one clear next step instead of a raw
// API string. Billing → fix the card; config (e.g. an objective/destination
// mismatch) → edit the campaign; setup → finish account setup.
export function classifyLaunchFailure(message: string): { kind: LaunchFailureKind; hint: string } {
  const text = message.toLowerCase();
  if (/card|funds|payment method|billing|charge|balance|declined/.test(text)) {
    return { kind: "billing", hint: "Add or fix this account's payment card, then launch again." };
  }
  if (/not supported|objective|destination|messenger|whatsapp|instagram|creative|budget|minimum|audience|placement|headline|caption/.test(text)) {
    return { kind: "config", hint: "This is a campaign setup conflict. Edit the campaign to resolve it, then launch again." };
  }
  if (/identity|facebook page|social account|pixel|connect|not ready|finish/.test(text)) {
    return { kind: "setup", hint: "Finish the flagged account setup, then launch again." };
  }
  return { kind: "generic", hint: "Adjust the campaign and launch again." };
}

export function DraftCampaignLauncher({ revision, onLaunched, onBilling, onEdit }: {
  revision: number;
  onLaunched: () => void;
  onBilling: () => void;
  onEdit: () => void;
}) {
  const [campaigns, setCampaigns] = useState<DraftCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [confirmingId, setConfirmingId] = useState("");
  const [launchingId, setLaunchingId] = useState("");
  const [launchErrors, setLaunchErrors] = useState<Record<string, string>>({});
  const [launchedTitle, setLaunchedTitle] = useState("");

  const loadDrafts = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await api<DraftCampaignsResponse>("/api/ads/campaigns/drafts");
      setCampaigns(response.campaigns);
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : "Draft campaigns could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    setConfirmingId("");
    setLaunchErrors({});
    void api<DraftCampaignsResponse>("/api/ads/campaigns/drafts")
      .then((response) => { if (active) setCampaigns(response.campaigns); })
      .catch((caught) => {
        if (active) setLoadError(caught instanceof Error ? caught.message : "Draft campaigns could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [revision]);

  const launch = async (campaign: DraftCampaign) => {
    setLaunchingId(campaign.id);
    setLaunchErrors((current) => ({ ...current, [campaign.id]: "" }));
    setLaunchedTitle("");
    try {
      const response = await api<LaunchCampaignResponse>(`/api/ads/campaigns/${encodeURIComponent(campaign.id)}/launch`, { method: "POST" });
      setCampaigns((current) => current.filter((item) => item.id !== campaign.id));
      setConfirmingId("");
      setLaunchedTitle(response.campaign.title || campaign.title);
      onLaunched();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "The campaign could not be launched.";
      setLaunchErrors((current) => ({ ...current, [campaign.id]: message }));
    } finally {
      setLaunchingId("");
    }
  };

  if (loading) return <section className="draft-launcher draft-launcher--loading" aria-label="Loading draft campaigns"><LoaderCircle className="spin" /><span>Checking draft campaigns…</span></section>;
  if (loadError) return <section className="draft-launcher draft-launcher--error" role="alert"><AlertCircle /><div><strong>Draft campaigns could not be checked</strong><span>{loadError}</span></div><button className="button button--secondary button--small" type="button" onClick={() => void loadDrafts()}><RotateCw /> Retry</button></section>;
  if (!campaigns.length && !launchedTitle) return null;

  return <section className="draft-launcher" aria-label="Draft campaign launch controls">
    <div className="draft-launcher__heading"><span><Rocket /></span><div><strong>{campaigns.length ? "Draft campaigns ready to launch" : "Campaign launched"}</strong><p>{campaigns.length ? "Launch through the portal to see the exact account response if a campaign needs attention." : `${launchedTitle} was sent for activation.`}</p></div></div>
    {launchedTitle && <div className="draft-launcher__success" role="status"><Check /><span><strong>{launchedTitle}</strong> was launched successfully.</span></div>}
    {campaigns.map((campaign) => {
      const error = launchErrors[campaign.id] || "";
      const confirming = confirmingId === campaign.id;
      const launching = launchingId === campaign.id;
      return <article className="draft-launcher__campaign" key={campaign.id}>
        <div className="draft-launcher__campaign-main"><span className="draft-launcher__status">Draft</span><strong>{campaign.title || "Untitled campaign"}</strong></div>
        {!confirming && <button className="button button--primary button--small" type="button" disabled={Boolean(launchingId)} onClick={() => { setConfirmingId(campaign.id); setLaunchErrors((current) => ({ ...current, [campaign.id]: "" })); }}><Rocket /> Launch</button>}
        {confirming && <div className="draft-launcher__confirmation"><p>Confirm launch. Campaign spend begins after activation, and your card may see a temporary verification hold.</p><div><button className="button button--secondary button--small" type="button" disabled={launching} onClick={() => setConfirmingId("")}>Cancel</button><button className="button button--primary button--small" type="button" disabled={launching} onClick={() => void launch(campaign)}>{launching ? <LoaderCircle className="spin" /> : <Rocket />}{launching ? "Launching…" : "Confirm launch"}</button></div></div>}
        {error && (() => {
          const { kind, hint } = classifyLaunchFailure(error);
          return <div className="draft-launcher__failure" role="alert"><AlertCircle /><div><strong>This campaign was not launched</strong><span>{error}</span><span className="draft-launcher__hint">{hint}</span><div className="draft-launcher__failure-actions">{kind === "billing" ? <button type="button" onClick={onBilling}><CreditCard /> Review billing</button> : <button type="button" onClick={onEdit}><Pencil /> Edit campaign</button>}</div></div></div>;
        })()}
      </article>;
    })}
  </section>;
}
